import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const COLORS = { present: "bg-[#15803D] text-white", "half-day": "bg-[#B45309] text-white", absent: "bg-[#B91C1C] text-white" };
const ym = (d) => d.slice(0, 7);

function tally(recs) {
  return {
    present: recs.filter((r) => r.status === "present").length,
    half: recs.filter((r) => r.status === "half-day").length,
    absent: recs.filter((r) => r.status === "absent").length,
  };
}

function MonthCalendar({ month, byDate, onPrev, onNext }) {
  const [y, m] = month.split("-").map(Number);
  const days = new Date(y, m, 0).getDate();
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div data-testid="emp-att-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onPrev} className="p-2 border border-gray-300 rounded-sm hover:bg-gray-50" data-testid="emp-att-prev-month"><ChevronLeft size={16} /></button>
        <div className="font-heading font-black text-lg" data-testid="emp-att-month-label">{MONTHS[m - 1]} {y}</div>
        <button onClick={onNext} className="p-2 border border-gray-300 rounded-sm hover:bg-gray-50" data-testid="emp-att-next-month"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-gray-400 mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const date = `${month}-${String(d).padStart(2, "0")}`;
          const r = byDate[date];
          return (
            <div key={date} title={r ? `${r.status}${r.marked_by ? ` · marked by ${r.marked_by}` : ""}` : "Not marked"}
              className={`h-10 sm:h-12 rounded-sm flex items-center justify-center text-sm font-semibold ${r ? COLORS[r.status] : "bg-gray-100 text-gray-400"}`}
              data-testid={`emp-att-day-${date}`}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Totals({ t, marked, days }) {
  const box = (label, val, cls, id) => (
    <div><div className="small-label text-gray-500">{label}</div><div className={`data-num text-2xl ${cls}`} data-testid={id}>{val}</div></div>
  );
  return (
    <div className="grid grid-cols-4 gap-2 card-flat p-3">
      {box("Present", t.present, "text-[#15803D]", "emp-att-total-present")}
      {box("Half", t.half, "text-[#B45309]", "emp-att-total-half")}
      {box("Absent", t.absent, "text-[#B91C1C]", "emp-att-total-absent")}
      {box("Unmarked", Math.max(0, days - marked), "text-gray-400", "emp-att-total-unmarked")}
    </div>
  );
}

function MonthTable({ months, byMonth, active, onPick }) {
  return (
    <table className="w-full text-sm" data-testid="emp-att-month-table">
      <thead><tr className="text-left small-label text-gray-500"><th className="py-2">Month</th><th>P</th><th>H</th><th>A</th><th>Days worked</th></tr></thead>
      <tbody>
        {months.map((mo) => {
          const t = tally(byMonth[mo] || []);
          const [y, m] = mo.split("-").map(Number);
          return (
            <tr key={mo} onClick={() => onPick(mo)} data-testid={`emp-att-month-row-${mo}`}
              className={`border-t border-gray-200 cursor-pointer hover:bg-gray-50 ${mo === active ? "bg-gray-100 font-semibold" : ""}`}>
              <td className="py-2">{MONTHS[m - 1]} {y}</td>
              <td className="text-[#15803D]">{t.present}</td><td className="text-[#B45309]">{t.half}</td><td className="text-[#B91C1C]">{t.absent}</td>
              <td className="data-num">{t.present + t.half * 0.5}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const shiftMonth = (mo, n) => {
  const [y, m] = mo.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const EmployeeAttendanceHistory = ({ employeeId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    setData(null); setError("");
    api.get(`/attendance/employee/${employeeId}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [employeeId]);

  const { byDate, byMonth, months } = useMemo(() => {
    const recs = data?.records || [];
    const bd = {}, bm = {};
    recs.forEach((r) => { bd[r.date] = r; (bm[ym(r.date)] = bm[ym(r.date)] || []).push(r); });
    return { byDate: bd, byMonth: bm, months: Object.keys(bm).sort().reverse() };
  }, [data]);

  const [y, m] = month.split("-").map(Number);
  const monthRecs = byMonth[month] || [];
  return (
    <div className="card-flat p-5 space-y-5" data-testid="emp-att-history">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="small-label text-gray-500">Attendance history</div>
          <div className="font-heading font-black text-xl" data-testid="emp-att-name">{data?.employee?.name || "…"}</div>
          {data && <div className="text-xs text-gray-500">{data.records.length} days marked in total</div>}
        </div>
        <button onClick={onClose} className="p-2 border border-gray-300 rounded-sm hover:bg-gray-50" data-testid="emp-att-close"><X size={16} /></button>
      </div>
      {error && <div className="text-sm text-[#B91C1C]">{error}</div>}
      {!data && !error && <div className="text-sm text-gray-400">Loading…</div>}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <MonthCalendar month={month} byDate={byDate} onPrev={() => setMonth(shiftMonth(month, -1))} onNext={() => setMonth(shiftMonth(month, 1))} />
            <Totals t={tally(monthRecs)} marked={monthRecs.length} days={new Date(y, m, 0).getDate()} />
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {months.length ? <MonthTable months={months} byMonth={byMonth} active={month} onPick={setMonth} />
              : <div className="text-sm text-gray-500">No attendance marked yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
};
